// Where a stacked parent keeps its secondary rows (DataTable's getSubRows)
export const SUB_ROWS_KEY = '__subRows';

export const getSecondarySubRows = (
  row: DataTable.Row,
): DataTable.Row[] | undefined => row[SUB_ROWS_KEY];

// Values a row of a latest-value column shows that aren't values
const isShownValue = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '' && value !== '-';

// How many different values a parent's rows show in a column, as the
// secondary column counts them, or '-' when they show none
const toDistinctCount = ({
  children,
  column,
}: {
  children: DataTable.Row[];
  column: string;
}): string => {
  const values = new Set(
    children.map((row) => row[column]).filter(isShownValue),
  );
  return values.size > 0 ? `(${values.size})` : '-';
};

// A matrix's secondary rows stacked under their dimension A value: one parent
// per value, in the order the values first appear in the rows, carrying that
// value's subtotals (its row from the subtotals query) and, in the secondary
// column, how many rows it holds. A value with no subtotals row still gets a
// parent, so none of its rows is lost.
// Columns of a single latest value per row (a 'first' or 'last' metric, e.g.
// a field column) have no subtotal: their parent shows how many different
// values its rows have instead.
const stackSecondaryRows = ({
  parentRows,
  rows,
  field,
  secondaryField,
  distinctCountColumns = [],
}: {
  parentRows: DataTable.Row[];
  rows: DataTable.Row[];
  field: string;
  secondaryField: string;
  distinctCountColumns?: string[];
}): DataTable.Row[] => {
  const childrenByValue = new Map<string, DataTable.Row[]>();
  rows.forEach((row) => {
    const value = `${row[field]}`;
    const children = childrenByValue.get(value);
    if (children) {
      children.push(row);
    } else {
      childrenByValue.set(value, [row]);
    }
  });
  const parentByValue = new Map(
    parentRows.map((parent) => [`${parent[field]}`, parent]),
  );

  return Array.from(childrenByValue.entries()).map(([value, children]) => ({
    ...(parentByValue.get(value) ?? { [field]: children[0][field] }),
    ...Object.fromEntries(
      distinctCountColumns.flatMap((column) => [
        [column, toDistinctCount({ children, column })],
        // Nothing to colour or sort by but the count
        [`${column}-raw`, undefined],
      ]),
    ),
    [secondaryField]: `(${children.length})`,
    [SUB_ROWS_KEY]: children,
  }));
};

export default stackSecondaryRows;
